import { useEffect, useRef, useState } from 'react';
import { Route, Routes, useLocation } from 'react-router-dom';
import styles from './AppContainer.module.scss';
import { usePluginStore } from 'react-pluggable';
import Header from '../../shared/header/Header';
import CmsContainer from '../cms-container/CmsContainer';
import Register from '../../auth/register/Register';
import Login from '../../auth/login/Login';
import {
    CALCULATION_SHIPPING_COSTS,
    CAREER,
    CATALOG,
    COMPANY,
    CONTACT_US,
    ENGINEERING,
    SERVICES,
    SHIPMENT_CONDITIONS,
    VISIT_US,
} from '../../shared/constants/cms-codes.constants';
import { CmsTitlesAndUrls } from '../cms-container/models/cms-titles-url-by-code';
import { getCmsUrlAndTitleByLanguagAndCode } from '../cms-container/services';
import ProductsFirstLevel from '../products-first-level/ProductsFirstLevel';
import ProductsSecondLevel from '../products-second-level/ProductsSecondLevel';
import * as authService from '../../auth/services/auth.service';
import LoginOrRegisterFixcenter from '../../components/login-or-register-fixcenter/LoginOrRegisterFixcenter';
import ShoppingCartContainer from '../shopping-cart-container/ShoppingCartContainer';
import MyInvoicesContainer from '../my-invoices-container/MyInvoicesContainer';
import MyOffersContainer from '../my-offers-container/MyOffersContainer';
import AddBuildingSiteForm from '../my-building-sites/add-building-site-form/AddBuildingSiteForm';
import ResetPassword from '../../auth/reset-password/components/reset-password/ResetPassword';
import {
    getLinkToContactForm,
    getLinkToContactSuccess,
    getLinkToVisitUs,
} from '../../components/fixcenter-support/fixcenter-support.service';
import ContactUsForm from '../../components/fixcenter-support/ContactUsForm';
import ContactUsSuccess from '../../components/fixcenter-support/ContactUsSuccess';
import {
    getLinkToAccountManagerJobOffer,
    getLinkToAccountManagerLuxJobOffer,
    getLinkToOpenJobOffers,
    getLinkToRevitDesignerJobOffer,
    getLinkToSalesChauffeurJobOffer,
} from '../../components/fixcenter-career/fixcenter-career.service';
import CareerJobOffers from '../../components/fixcenter-career/fixcenter-career-job-offers';
import CareerSalesChauffeurJobOffer from '../../components/fixcenter-career/fixcenter-career-sales-chauffeur-job-offer';
import CareerRevitDesignerJobOffer from '../../components/fixcenter-career/fixcenter-career-revit-designer-job-offer';
import CareerAccountManagerJobOffer from '../../components/fixcenter-career/fixcenter-career-account-manager-job-offer';
import CareerAccountManagerLuxJobOffer from '../../components/fixcenter-career/fixcenter-career-account-manager-lux-job-offer';
import globalStyles from '../../global-styles.module.scss';
import OrderConfirmation from '../shopping-cart-container/order-confirmation/order-confirmation';
import {
    BUILDING_SITES_PATHS,
    DOCUMENTS_DELIVERY,
    FAVORITE_PRODUCTS_PATHS,
    FORGOTTEN_PASSWORD_PATHS,
    ORDER_PATHS,
    SHOPPING_CART_PATHS,
    USER_PANEL_PATHS,
    PROCUREMENT_LOGIN,
} from '../../shared/constants/breadcrumb.constants';
import OrderConfirmationSuccess from '../shopping-cart-container/order-confirmation/order-confirmatio-success';
import FavoriteProducts from '../favorite-products/favorite-products';
import ProductDirectory from '../product-directory/product-directory';
import LoginFormComponent from '../../plugins/components/LoginForm/LoginForm';
import { getCmsLinkToCatalog } from '../cms-container/services/cms-navigation.service';
import {
    getLinkToBuildingSitesForm,
    getLinkToEditBuildingSitesForm,
    getLinkToViewBuildingSitesForm,
} from '../shopping-cart-container/building-sites/building-sites-form.service';
import BuildingSitesForm from '../shopping-cart-container/building-sites/building-sites-form';
import ProductsThirdLevelWrapper from '../products-third-level/ProductsThirdLevelWrapper';
import BuildingSiteList from '../building-sites-list/building-sites-list';
import ResetPasswordForm from '../../auth/reset-password/components/set-new-password/reset-password-form';
import UserPanel from '../../components/user-panel/user-panel';
import NotSearchResultsPage from '../search-results-page/NotSearchResultsPage';
import SearchResultsPageWrapper from '../search-results-page/SearchResultsPageWrapper';
import { DocumentType } from '../../models/documentType.model';
import DocumentListView from '../documents/document-list-view/document-list-view';
import ProcurementLogin from '../../auth/procurement-login/ProcurementLogin';

export default function AppContainer() {
    const pluginStore: any = usePluginStore();
    let Renderer = pluginStore.executeFunction('Renderer.getRendererComponent');
    const [cartItemsChanged, setCartItemsChanged] = useState<boolean>(false);
    const [titlesAndUrls, setTitlesAndUrls] = useState<CmsTitlesAndUrls>({});
    const [loading, setLoading] = useState(true);
    const cmsContainerRef = useRef<HTMLElement | null>(null);
    const location = useLocation();
    const [cmsContainerLoaded, setCmsContainerLoaded] = useState(false);

    const getTitleAndUrlForHeader = async (): Promise<void[]> => {
        const codes = [
            SERVICES,
            COMPANY,
            CAREER,
            CONTACT_US,
            ENGINEERING,
            VISIT_US,
            CATALOG,
            SHIPMENT_CONDITIONS,
            CALCULATION_SHIPPING_COSTS,
        ];
        const savedLanguageCode = localStorage.getItem('lang');
        if (savedLanguageCode) {
            const promises = codes.map(async (code) => {
                const response = await getCmsUrlAndTitleByLanguagAndCode(code, savedLanguageCode);
                setTitlesAndUrls((prevTitlesAndUrls) => ({
                    ...prevTitlesAndUrls,
                    [code]: response.data,
                }));
            });
            await Promise.all(promises);
            setLoading(false);
        }

        return Promise.resolve([]);
    };

    useEffect(() => {
        getTitleAndUrlForHeader();
    }, [localStorage.getItem('lang')]);

    useEffect(() => {}, [loading, titlesAndUrls]);

    const scrollToCmsContainer = () => {
        if (cmsContainerRef.current) {
            window.scrollTo({
                top: cmsContainerRef.current.offsetTop,
                behavior: 'smooth',
            });
        }
    };

    useEffect(() => {
        scrollToCmsContainer();
    }, [location.pathname, cmsContainerLoaded]);

    return (
        <div className={styles.main_container} id={'AppContainer'}>
            <div className={styles.header}>
                <Header cartItemsChanged={cartItemsChanged} />
            </div>
            {/* #region Importing images at application startup*/}
            <div className={globalStyles.fixcenter_support_visit_us_sprites}></div>
            <div className={globalStyles.icon_sprites}></div>
            {/* endregion*/}
            <div className={styles.content}>
                <Routes>
                    <Route path="/:language?" element={<Renderer placement="page:fixcenterSlider" />} />
                    <Route path="/:language?/login" Component={Login} />
                    <Route path="/:language?/register" Component={Register} />
                    <Route path="/*" element={<CmsContainer />} />
                    {
                        <Route
                            path="/:language?/webshop"
                            element={authService.checkLogInStatus() === true ? <ProductsFirstLevel /> : <Login />}
                        ></Route>
                    }
                    {
                        <Route
                            path="/:language?/webshop/category/:categoryName?/:categoryId?"
                            element={authService.checkLogInStatus() === true ? <ProductsSecondLevel /> : <Login />}
                        ></Route>
                    }
                    {
                        <Route
                            path="/:language?/webshop/category/:categoryName?/directory/:directoryName?/:categoryId?/:directoryId?"
                            element={authService.checkLogInStatus() === true ? <ProductDirectory /> : <Login />}
                        ></Route>
                    }
                    {
                        <Route
                            path="/:language?/webshop/product/:categoryName?/:categoryId?/:productId?"
                            element={
                                authService.checkLogInStatus() === true ? <ProductsThirdLevelWrapper /> : <Login />
                            }
                        ></Route>
                    }
                    {
                        <Route
                            path="/:language?/webshop/search/:value?"
                            element={authService.checkLogInStatus() === true ? <SearchResultsPageWrapper /> : <Login />}
                        ></Route>
                    }
                    <Route
                        path={`/:language?${titlesAndUrls.SERVICES?.url}`}
                        element={<CmsContainer code={SERVICES} />}
                    ></Route>
                    {
                        <Route
                            path={`/:language?${titlesAndUrls.CATALOG?.url}`}
                            element={
                                authService.checkLogInStatus() === true ? (
                                    <CmsContainer code={CATALOG} />
                                ) : (
                                    <LoginFormComponent redirectUrl={getCmsLinkToCatalog()} />
                                )
                            }
                        />
                    }
                    <Route
                        path={`/:language?${titlesAndUrls.COMPANY?.url}`}
                        element={<CmsContainer code={COMPANY} />}
                    ></Route>
                    <Route
                        path={`/:language?${titlesAndUrls.CAREER?.url}`}
                        element={<CmsContainer code={CAREER} />}
                    ></Route>
                    <Route
                        path={`/:language?${titlesAndUrls.CONTACT_US?.url}`}
                        element={<CmsContainer code={CONTACT_US} />}
                    ></Route>
                    <Route
                        path={`/:language?${titlesAndUrls.ENGINEERING?.url}`}
                        element={<CmsContainer code={ENGINEERING} />}
                    ></Route>
                    <Route
                        path={`/:language?${titlesAndUrls.SHIPMENT_CONDITIONS?.url}`}
                        element={<CmsContainer code={SHIPMENT_CONDITIONS} />}
                    ></Route>
                    <Route
                        path={`/:language?${titlesAndUrls.CALCULATION_SHIPPING_COSTS?.url}`}
                        element={<CmsContainer code={CALCULATION_SHIPPING_COSTS} />}
                    ></Route>
                    <Route path={`/:language?/Home/Product`} element={<LoginOrRegisterFixcenter />}></Route>
                    {
                        <Route
                            path={`/:language?${getLinkToContactForm()}`}
                            element={<ContactUsForm isFromContactUs={true} />}
                        />
                    }
                    {<Route path={`/:language?${getLinkToContactSuccess()}`} element={<ContactUsSuccess />} />}
                    {
                        <Route
                            path={`/:language?${getLinkToVisitUs()}`}
                            element={<ContactUsForm isFromContactUs={false} />}
                        />
                    }
                    {<Route path={`/:language?${getLinkToOpenJobOffers()}`} element={<CareerJobOffers />} />}
                    {
                        <Route
                            path={`/:language?${getLinkToSalesChauffeurJobOffer()}`}
                            element={<CareerSalesChauffeurJobOffer />}
                        />
                    }
                    {
                        <Route
                            path={`/:language?${getLinkToRevitDesignerJobOffer()}`}
                            element={<CareerRevitDesignerJobOffer />}
                        />
                    }
                    {
                        <Route
                            path={`/:language?${getLinkToAccountManagerJobOffer()}`}
                            element={<CareerAccountManagerJobOffer />}
                        />
                    }
                    {
                        <Route
                            path={`/:language?${getLinkToAccountManagerLuxJobOffer()}`}
                            element={<CareerAccountManagerLuxJobOffer />}
                        />
                    }
                    {
                        <Route
                            path={`/:language?${SHOPPING_CART_PATHS[1].link}`}
                            element={authService.checkLogInStatus() === true ? <ShoppingCartContainer /> : <Login />}
                        />
                    }
                    {
                        <Route
                            path={`/:language?${USER_PANEL_PATHS[0].link}`}
                            element={authService.checkLogInStatus() === true ? <UserPanel /> : <Login />}
                        />
                    }
                    {
                        <Route
                            path={`/:language?${ORDER_PATHS[0].link}`}
                            element={authService.checkLogInStatus() === true ? <OrderConfirmation /> : <Login />}
                        />
                    }
                    {
                        <Route
                            path={`/:language?${ORDER_PATHS[1].link}`}
                            element={authService.checkLogInStatus() === true ? <OrderConfirmationSuccess /> : <Login />}
                        />
                    }
                    {
                        <Route
                            path={`/:language?/search/not-found`}
                            element={authService.checkLogInStatus() === true ? <NotSearchResultsPage /> : <Login />}
                        />
                    }
                    {
                        <Route
                            path={`/:language?${FAVORITE_PRODUCTS_PATHS[0].link}`}
                            element={authService.checkLogInStatus() === true ? <FavoriteProducts /> : <Login />}
                        />
                    }
                    {
                        <Route
                            path={`/:language?${BUILDING_SITES_PATHS[0].link}`}
                            element={authService.checkLogInStatus() === true ? <BuildingSiteList /> : <Login />}
                        />
                    }
                    {
                        <Route
                            path={`/:language?${DOCUMENTS_DELIVERY[2].link}`}
                            element={
                                authService.checkLogInStatus() === true ? (
                                    <DocumentListView
                                        fetchPerUserOnly={false}
                                        documentTypeCodes={[DocumentType.InvoiceProforma, DocumentType.CreditNote]}
                                    />
                                ) : (
                                    <Login />
                                )
                            }
                        />
                    }
                    {
                        <Route
                            path={`/:language?/Cart/Order/List`}
                            element={
                                authService.checkLogInStatus() === true ? (
                                    <DocumentListView
                                        fetchPerUserOnly={false}
                                        documentTypeCodes={[DocumentType.ClientOrder]}
                                    />
                                ) : (
                                    <Login />
                                )
                            }
                        />
                    }
                    {
                        <Route
                            path={`/:language?${getLinkToBuildingSitesForm()}`}
                            element={
                                authService.checkLogInStatus() === true ? (
                                    <BuildingSitesForm isEdit={false} readonly={false} />
                                ) : (
                                    <Login />
                                )
                            }
                        />
                    }
                    {
                        <Route
                            path={`/:language?${getLinkToEditBuildingSitesForm()}`}
                            element={
                                authService.checkLogInStatus() === true ? (
                                    <BuildingSitesForm isEdit={true} readonly={false} />
                                ) : (
                                    <Login />
                                )
                            }
                        />
                    }
                    {
                        <Route
                            path={`/:language?${getLinkToViewBuildingSitesForm()}`}
                            element={
                                authService.checkLogInStatus() === true ? (
                                    <BuildingSitesForm isEdit={true} readonly={true} />
                                ) : (
                                    <Login />
                                )
                            }
                        />
                    }
                    {/* {<Route path={`/:language?/Cart/BackOrder/List`} element={authService.checkLogInStatus() === true ? <MyOrdersContainer /> : <Login />} />} */}
                    {
                        <Route
                            path={`/:language?/Cart/Invoices/List`}
                            element={authService.checkLogInStatus() === true ? <MyInvoicesContainer /> : <Login />}
                        />
                    }
                    {
                        <Route
                            path={`/:language?/Cart/Offers/List`}
                            element={authService.checkLogInStatus() === true ? <MyOffersContainer /> : <Login />}
                        />
                    }
                    {/* {<Route path={`/:language?/Cart/BuildingSites/List`} element={authService.checkLogInStatus() === true ? <MyBuildingSites /> : <Login />} />} */}
                    {
                        <Route
                            path={`/:language?/BuildingSites/AddBuildingSite`}
                            element={authService.checkLogInStatus() === true ? <AddBuildingSiteForm /> : <Login />}
                        />
                    }
                    {<Route path={`/:language?${FORGOTTEN_PASSWORD_PATHS[0].link}`} element={<ResetPassword />} />}
                    {<Route path="/:language?/reset-password/*" element={<ResetPasswordForm />} />}
                    <Route
                        path={`/:language?/${PROCUREMENT_LOGIN.PUNCHOUT}`}
                        element={<ProcurementLogin loginUrl={PROCUREMENT_LOGIN.PUNCHOUT} />}
                    />
                    <Route
                        path={`/:language?/${PROCUREMENT_LOGIN.CEGELEC}`}
                        element={<ProcurementLogin loginUrl={PROCUREMENT_LOGIN.CEGELEC} />}
                    />
                    <Route
                        path={`/:language?/${PROCUREMENT_LOGIN.PLENION}`}
                        element={<ProcurementLogin loginUrl={PROCUREMENT_LOGIN.PLENION} />}
                    />
                </Routes>
            </div>
            <div className={styles.footer}>
                <Renderer placement="page:fixcenterFooter" />
            </div>
        </div>
    );
}
